import { useMemo } from 'react';
import { paths } from 'src/routes/paths';
import { useLocales } from 'src/locales';
import Iconify from 'src/components/iconify';
import SvgColor from 'src/components/svg-color';
import { useAuthContext } from 'src/auth/hooks';
import useSWR from 'swr';
import { fetcher } from 'src/utils/axios';

const icon = (name) => (
  <SvgColor src={`/static/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  job: icon('ic_job'),
  courses: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  student: icon('ic_user'),
  batch: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
};

const NAV_MODULE_MAPPING = {
  user: 'user',
  student: 'student',
  courses: 'course',
  course: 'course',
  fee: 'fee',
  'batch management': 'batch',
  batch: 'batch',
  hostel: 'hostel',
  'vacated-bed-list': 'hostel',
  'vacated bed list': 'hostel',
  refund: 'refund',
  'vacate-refund-list': 'refund',
  'vacation refund': 'refund',
  attendance: 'attendance',
  register: 'register',
  'cash register': 'cash register',
  report: 'report',
  tax: 'tax',
  'hostel & mess': 'hostel & mess',
  template: 'template',
  'sms template': 'sms_template',
  'sms-template': 'sms_template',
  'email template': 'sms_template',
  'email-template': 'sms_template',
  'sms category': 'sms_template',
  'sms-category': 'sms_template',
  center: 'center',
  school: 'school',
  schools: 'school',
  classes: 'classes',
  tags: 'tags',
  subjects: 'subjects',
  target: 'target',
  discount: 'discount',
  examination: 'examination',
  pattern: 'pattern',
  building: 'building',
  teacher: 'teacher',
  syllabus: 'syllabus',
  'question bank': 'questionbank',
  questionbank: 'questionbank',
  'exam result': 'result',
  result: 'result',
  talenthunt: 'talenthunt',
  'talent hunt': 'talenthunt',
  'financial report': 'financial report',
  'student report': 'student report',
  'addmission report': 'admission report',
  'admission report': 'admission report',
  'batch report': 'batch report',
  'examination report': 'examination report',
  timetable: 'timetable',
  'communication logs': 'communication logs',
  'create communication': 'communication logs',
  communication: 'communication logs',
  dat: 'dat',
  'question difficulty configuration': 'question difficulty',
  'question difficulty': 'question difficulty',
  session: 'session',
  organization: 'organization',
  qr: 'qr',
  roles: 'roles',
  outsider: 'outsider',
  assignment: 'assignment',
  'gst report': 'gst report',
  task: 'task',
  tasks: 'task',
  'task list': 'task',
  refund: 'refund',
  'vacate-refund-list': 'refund',
  'vacation refund': 'refund',
  bank: 'bank',
  banks: 'bank',
  'hostel & mess balance report': 'student report',
  'hostel mess balance report': 'student report',
};

export function useNavData() {
  const { user } = useAuthContext();
  const { t } = useLocales();

  const isHeightStudent = Boolean(user?.is_height_student || user?.heightstudent_id);

  const { data: heightStudentProfile } = useSWR(
    isHeightStudent ? '/talent-hunt/api/talent-hunt-exams/student-profile/' : null,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnMount: true,
    }
  );

  const isHeightResultDeclared = useMemo(() => {
    if (user?.is_result_declared || user?.declare_result) return true;
    if (heightStudentProfile?.is_result_declared || heightStudentProfile?.declare_result) return true;
    if (Array.isArray(heightStudentProfile?.enrollments)) {
      return heightStudentProfile.enrollments.some(
        (enr) =>
          enr?.declare_result ||
          enr?.exam_details?.declare_result ||
          enr?.talent_hunt_exam?.declare_result
      );
    }
    if (Array.isArray(heightStudentProfile?.assignments)) {
      return heightStudentProfile.assignments.some(
        (asg) => asg?.declare_result || asg?.talent_hunt_exam?.declare_result
      );
    }
    return false;
  }, [user, heightStudentProfile]);

  const getModuleName = (title) => {
    const rawTitle = title.toLowerCase();
    return NAV_MODULE_MAPPING[rawTitle] || null;
  };

  const hasPermission = (moduleName, permissionType = 'can_view') => {
    if (user?.is_admin) return true;

    if (!user?.is_non_academic || !user?.non_academic_role_permissions) return false;

    const rawModuleName = moduleName ? moduleName.toLowerCase() : '';
    const resolvedModuleName = NAV_MODULE_MAPPING[rawModuleName] || rawModuleName;

    const permission = user.non_academic_role_permissions.find(
      (perm) =>
        perm.module &&
        (perm.module.toLowerCase() === resolvedModuleName ||
         perm.module.toLowerCase() === rawModuleName)
    );

    if (permissionType === 'hostel_mess_balance_report' || permissionType === 'hostel_&_mess_balance_report') {
      if (permission && (permission.hostel_mess_balance_report || permission['hostel_&_mess_balance_report'])) {
        return true;
      }
      const studentReportPerm = user.non_academic_role_permissions.find(
        (p) => p.module && p.module.toLowerCase() === 'student report'
      );
      if (
        studentReportPerm &&
        (studentReportPerm.hostel_mess_balance_report ||
         studentReportPerm['hostel_&_mess_balance_report'] ||
         studentReportPerm.student_report ||
         studentReportPerm.can_view)
      ) {
        return true;
      }
    }

    if (!permission) {
      return false;
    }

    if (
      permissionType === 'tasks_view' ||
      permissionType === 'tasks' ||
      ((resolvedModuleName === 'task' || rawModuleName === 'task') &&
        (permissionType === 'can_view' || permissionType === 'view'))
    ) {
      return Boolean(permission.tasks_view || permission.tasks || permission.can_view || permission.view);
    }
    if (permissionType === 'student_report') {
      return Boolean(
        permission.student_report ||
        permission.balance_report ||
        permission.hostel_mess_balance_report ||
        permission['hostel_&_mess_balance_report'] ||
        permission.report_card ||
        permission.student_test_analysis ||
        permission.student_question_wise_analysis ||
        permission.can_view
      );
    }
    if (permissionType === 'hostel_mess_balance_report' || permissionType === 'hostel_&_mess_balance_report') {
      return Boolean(
        permission.hostel_mess_balance_report ||
        permission['hostel_&_mess_balance_report'] ||
        permission.student_report ||
        permission.can_view
      );
    }
    return Boolean(permission[permissionType]);
  };
  const academicData = useMemo(
    () => [
      {
        subheader: t('management'),
        items: [
          {
            title: t('Dashboard'),
            roles: ['academic'],
            path: paths.academicdashboard.root,
            icon: ICONS.dashboard,
          },
          {
            title: t('Examination'),
            roles: ['academic'],
            path: paths.academicdashboard.examination.root,
            icon: <Iconify icon="healthicons:i-exam-multiple-choice" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.academicdashboard.examination.root },
              { title: t('logs'), path: paths.academicdashboard.examination.log },
              { title: t('create'), path: paths.academicdashboard.examination.new },
              {
                title: t('Pattern'),
                path: paths.academicdashboard.pattern.new,
                children: [
                  { title: t('List'), path: paths.academicdashboard.pattern.list },
                  { title: t('Create'), path: paths.academicdashboard.pattern.new },
                ],
              },
              {
                title: t('Exam Result'),
                path: paths.academicdashboard.offlineexam.new,
                children: [
                  { title: t('List'), path: paths.academicdashboard.offlineexam.list },
                  { title: t('Create'), path: paths.academicdashboard.offlineexam.new },
                ],
              },
            ],
          },
          {
            title: t('timetable'),
            roles: ['academic'],
            path: paths.academicdashboard.teachertimetable.root,
            icon: <Iconify icon="ph:calendar-bold" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.academicdashboard.teachertimetable.list },

            ],
          },
          {
            title: t('syllabus'),
            path: paths.academicdashboard.syllabus.root,
            roles: ['academic'],
            icon: <Iconify icon="hugeicons:task-edit-01" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.academicdashboard.syllabus.list },
              { title: t('create'), path: paths.academicdashboard.syllabus.new },
            ],
          },
          {
            title: t('Question Bank'),
            roles: ['academic'],
            path: paths.academicdashboard.questionbank.root,
            icon: <Iconify icon="si:money-line" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.academicdashboard.questionbank.root },
              { title: t('setup'), path: paths.academicdashboard.questionbank.new },
              { title: t('tasks'), path: paths.academicdashboard.task.list },
            ],
          },
          {
            title: t('assignment'),
            path: paths.academicdashboard.assignment.root,
            roles: ['academic'],
            icon: <Iconify icon="si:assignment-line" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.academicdashboard.assignment.list },
            ],
          },
          {
            title: t('Report'),
            roles: ['academic'],
            path: paths.academicdashboard.studentreportcard.list,
            icon: <Iconify icon="carbon:report" height={22} width={22} />,
            children: [
              {
                title: t('Student Report'),
                path: paths.academicdashboard.studentreportcard.list,
                children: [
                  { title: t('Report Card'), path: paths.academicdashboard.studentreportcard.list },
                  { title: t('Student Test Analysis'), path: paths.academicdashboard.studenttestanalysis.list },
                  { title: t('Question Wise Analysis'), path: paths.academicdashboard.studentqueswiseanalysis.list },
                ],
              },
              {
                title: t('Batch Report'),
                path: paths.academicdashboard.batchtopper.list,
                children: [
                  { title: t('Batch Topper'), path: paths.academicdashboard.batchtopper.list },
                ],
              },
              {
                title: t('Examination Report'),
                path: paths.academicdashboard.testresultreport.list,
                children: [
                  { title: t('Test Result'), path: paths.academicdashboard.testresultreport.list },
                  { title: t('Question Wise Analysis'), path: paths.academicdashboard.queswiseanalysis.list },
                  { title: t('Quiz Subject Marks'), path: paths.academicdashboard.quizsubjectmarksreport.list },
                ],
              },
            ],
          },
        ],
      },
    ],
    [t]
  );

  const adminData = useMemo(
    () => [
      {
        subheader: t('management'),
        items: [
          {
            title: t('user'),
            roles: ['admin'],
            path: paths.dashboard.user.list,
            icon: <Iconify icon="bxs:user" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.dashboard.user.list },
              {
                title: t('create'),
                path: paths.dashboard.user.new,
                children: [
                  { title: t('academic user'), path: paths.dashboard.user.new },
                  { title: t('non-academic user'), path: paths.dashboard.user.new1 },
                ],
              },
              { title: t('logs'), path: paths.dashboard.user.logs },
            ],
          },
          {
            title: t('Building'),
            roles: ['admin'],
            path: paths.dashboard.building.root,
            icon: <Iconify icon="fluent:building-multiple-24-regular" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.dashboard.building.root },
              { title: t('create'), path: paths.dashboard.building.new },
            ],
          },
          {
            title: t('student'),
            roles: ['admin'],
            path: paths.dashboard.student.list,
            icon: <Iconify icon="ph:student-bold" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.dashboard.student.list },
              { title: t('create student'), path: paths.dashboard.student.new },
              { title: t('create without steps'), path: paths.dashboard.student.newWithoutSteps },
              { title: t('register'), path: paths.dashboard.student.register },
              { title: t('register-list'), path: paths.dashboard.registerlist.list },
              { title: t('bulk-upload'), path: paths.dashboard.student.bulkupload },
              { title: t('logs'), path: paths.dashboard.student.logs },
            ],
          },
          {
            title: t('refund'),
            roles: ['admin'],
            path: paths.dashboard.refund.root,
            icon: <Iconify icon="si:money-line" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.dashboard.refund.root },
              { title: t('create'), path: paths.dashboard.refund.new },
            ],
          },
          {
            title: t('timetable'),
            roles: ['admin'],
            path: paths.dashboard.timetable.list,
            icon: <Iconify icon="ph:calendar-bold" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.dashboard.timetable.list },
              { title: t('slots'), path: paths.dashboard.timetable.slots },

            ],
          },
          {
            title: t('courses'),
            roles: ['admin'],
            path: paths.dashboard.courses.root,
            icon: <Iconify icon="hugeicons:book-04" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.dashboard.courses.root },
              { title: t('create'), path: paths.dashboard.courses.new },
              { title: t('configuration'), path: paths.dashboard.courses.configure },
              { title: t('Add Material'), path: paths.dashboard.courses.material },
              { title: t('group'), path: paths.dashboard.courseGroup.root },
              { title: t('logs'), path: paths.dashboard.courses.logs },
            ],
          },
          // {
          //   title: t('LMS'),
          //   roles: ['admin'],
          //   path: paths.dashboard.lms.root,
          //   icon: <Iconify icon="carbon:education" height={22} width={22} />,
          //   children: [
          //     { title: t('LMS'), path: paths.dashboard.lms.root },
          //   ],
          // },
          {
            title: t('fee'),
            roles: ['admin'],
            path: paths.dashboard.fee.root,
            icon: <Iconify icon="si:money-line" height={22} width={22} />,
            children: [
              // { title: t('list'), path: paths.dashboard.fee.root },
              { title: t('Approval list'), path: paths.dashboard.chequeapproval.root },
              { title: t('Fee-Collection'), path: paths.dashboard.fee.new },
              { title: t('All Payments'), path: paths.dashboard.allpayments.list },
            ],
          },
          {
            title: t('batch management'),
            roles: ['admin'],
            path: paths.dashboard.batch.list,
            icon: <Iconify icon="material-symbols:table" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.dashboard.batch.list },
              { title: t('create'), path: paths.dashboard.batch.new },
              { title: t('logs'), path: paths.dashboard.batch.logs },
              {
                title: t('Batch Group'),
                path: paths.dashboard.batchgroup.list,
                children: [
                  { title: t('List'), path: paths.dashboard.batchgroup.list },
                  { title: t('Create'), path: paths.dashboard.batchgroup.new },
                ],
              },
              {
                title: t('teacher'),
                path: paths.dashboard.teacher.root,
                children: [
                  { title: t('list'), path: paths.dashboard.teacher.allocate },
                  { title: t('Unallocate'), path: paths.dashboard.teacher.unallocate },
                  { title: t('transfer'), path: paths.dashboard.teacher.transfer },
                  { title: t('logs'), path: paths.dashboard.teacher.log },
                ],
              },
            ],
          },
          {
            title: t('Examination'),
            roles: ['admin'],
            path: paths.dashboard.examination.root,
            icon: <Iconify icon="healthicons:i-exam-multiple-choice" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.dashboard.examination.root },
              { title: t('logs'), path: paths.dashboard.examination.log },
              { title: t('create'), path: paths.dashboard.examination.new },
              {
                title: t('Pattern'),
                path: paths.dashboard.pattern.new,
                children: [
                  { title: t('List'), path: paths.dashboard.pattern.list },
                  { title: t('Create'), path: paths.dashboard.pattern.new },
                ],
              },
              {
                title: t('Exam Result'),
                path: paths.dashboard.offlineexam.new,
                children: [
                  { title: t('List'), path: paths.dashboard.offlineexam.list },
                  { title: t('Create'), path: paths.dashboard.offlineexam.new },
                ],
              },
              {
                title: t('Examination Report'),
                path: paths.dashboard.testresultreport.list,
                children: [
                  { title: t('Test Result'), path: paths.dashboard.testresultreport.list },
                  { title: t('Question Wise Analysis'), path: paths.dashboard.queswiseanalysis.list },
                  { title: t('Quiz Subject Marks'), path: paths.dashboard.quizsubjectmarksreport.list },
                ],
              },
            ],
          },
          {
            title: t('talent hunt'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.talent_hunt.root,
            icon: <Iconify icon="material-symbols:trophy-outline" height={22} width={22} />,
            children: [
              { title: t('create'), path: paths.dashboard.height_student.new },
              { title: t('talent hunt exam'), path: paths.dashboard.talent_hunt.root },
              { title: t('Link Talent Hunt Exams'), path: paths.dashboard.talent_hunt.link_exams },
              { title: t('Admit Card Instructions'), path: paths.dashboard.talent_hunt.admit_card_instructions },
              { title: t('students'), path: paths.dashboard.talent_hunt.students },
              { title: t('result'), path: paths.dashboard.talent_hunt.result },
              { title: t('Schools'), path: paths.dashboard.school },
              { title: t('Talent Hunt Centers'), path: paths.dashboard.center },
            ],
          },
          {
            title: t('syllabus'),
            path: paths.dashboard.syllabus.root,
            roles: ['admin'],
            icon: <Iconify icon="hugeicons:task-edit-01" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.dashboard.syllabus.list },
              { title: t('create'), path: paths.dashboard.syllabus.new },
              { title: t('logs'), path: paths.dashboard.syllabus.logs },
            ],
          },
          {
            title: t('Question Bank'),
            roles: ['admin'],
            path: paths.dashboard.questionbank.root,
            icon: <Iconify icon="si:money-line" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.dashboard.questionbank.root },
              { title: t('setup'), path: paths.dashboard.questionbank.new },
              // { title: t('tasks'), path: paths.dashboard.task.list },
              { title: t('logs'), path: paths.dashboard.questionbank.logs },
            ],
          },
          {
            title: t('Hostel'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.hostel.root,
            icon: <Iconify icon="ph:building-bold" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.dashboard.hostel.root },
              { title: t('Campus list'), path: paths.dashboard.hostel.campuslist },
              { title: t('Hostel Text'), path: paths.dashboard.hostel.hostel_text },

              { title: t('create building'), path: paths.dashboard.hostel.new },
              { title: t('allocation'), path: paths.dashboard.hostel.allocation },
              { title: t('allocation-list'), path: paths.dashboard.hostel.allocationlist },
              { title: t('vacated-bed-list'), path: paths.dashboard.hostel.vacatedbedlist },
              { title: t('vacate-refund-list'), path: paths.dashboard.hostel.vacate_refund_list },
              {
                title: t('logs'),
                path: paths.dashboard.hostel.logs,
                children: [
                  { title: t('Hostel'), path: paths.dashboard.hostel.hostellogs },
                  { title: t('Allocation'), path: paths.dashboard.hostel.logs },
                  { title: t('Warden'), path: paths.dashboard.hostel.wardenlogs },
                ],
              },
              {
                title: t('outsider'),
                path: paths.dashboard.outsider.list,
                children: [
                  { title: t('list'), path: paths.dashboard.outsider.list },
                  { title: t('create'), path: paths.dashboard.outsider.new },
                  { title: t('logs'), path: paths.dashboard.outsider.logs },
                ],
              },
            ],
          },
          {
            title: t('attendance'),
            roles: ['admin'],
            path: paths.dashboard.attendanceadmin.list,
            icon: <Iconify icon="ph:student-bold" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.dashboard.attendanceadmin.list },
              { title: t('create'), path: paths.dashboard.attendanceadmin.new },
            ],
          },
          {
            title: t('Cash Register'),
            roles: ['admin'],
            path: paths.dashboard.cashregister.root,
            icon: <Iconify icon="mdi:cash" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.dashboard.register.root },
              { title: t('Transaction'), path: paths.dashboard.cashregister.root },
              { title: t('logs'), path: paths.dashboard.cashregister.logs },
              { title: t('Withdrawal Approvals'), path: paths.dashboard.cashregister.approvals },
              { title: t('Withdrawal Requests'), path: paths.dashboard.cashregister.requests },
            ],
          },
          {
            title: t('Report'),
            roles: ['admin'],
            path: paths.dashboard.report.list,
            icon: <Iconify icon="carbon:report" height={22} width={22} />,
            children: [
              {
                title: t('Financial Report'),
                path: paths.dashboard.report.list,
                children: [
                  { title: t('Cash register'), path: paths.dashboard.report.list },
                  { title: t('GST Report'), path: paths.dashboard.gstreport.list },
                  { title: t('Daily Collection Report'), path: paths.dashboard.dailycollectionreport.list },
                ],
              },
              {
                title: t('Student Report'),
                path: paths.dashboard.studentbalancereport.list,
                children: [
                  { title: t('Balance Report'), path: paths.dashboard.studentbalancereport.list },
                  { title: t('Hostel & Mess Balance Report'), path: paths.dashboard.hostel_mess_balance_report.list },
                  { title: t('Report Card'), path: paths.dashboard.studentreportcard.list },
                  { title: t('Student Test Analysis'), path: paths.dashboard.studenttestanalysis.list },
                  { title: t('Question Wise Analysis'), path: paths.dashboard.studentqueswiseanalysis.list },
                ],
              },
              {
                title: t('Admission Report'),
                path: paths.dashboard.addmissionsummary.list,
                children: [
                  { title: t('Admission Summary'), path: paths.dashboard.addmissionsummary.list },
                ],
              },
              {
                title: t('Batch Report'),
                path: paths.dashboard.batchtopper.list,
                children: [
                  { title: t('Batch Topper'), path: paths.dashboard.batchtopper.list },
                ],
              },
              {
                title: t('Examination Report'),
                path: paths.dashboard.testresultreport.list,
                children: [
                  { title: t('Test Result'), path: paths.dashboard.testresultreport.list },
                  { title: t('Question Wise Analysis'), path: paths.dashboard.queswiseanalysis.list },
                  { title: t('Quiz Subject Marks'), path: paths.dashboard.quizsubjectmarksreport.list },
                ],
              },
            ],
          },
          {
            title: t('Task List'),
            roles: ['admin'],
            path: paths.dashboard.task.list,
            icon: <Iconify icon="fluent-mdl2:task-manager" height={22} width={22} />,
            children: [
              { title: t('tasks'), path: paths.dashboard.task.list },
            ],
          },
          {
            title: t('Permissions'),
            path: paths.dashboard.permission_configure.root,
            icon: ICONS.lock,
            roles: ['admin'],
          },
          {
            title: t('Roles'),
            path: paths.dashboard.role,
            icon: <Iconify icon="eos-icons:role-binding" height={22} width={22} />,
            roles: ['admin'],
          },
          {
            title: t('communication'),
            path: paths.dashboard.communication.root,
            icon: <Iconify icon="healthicons:communication" height={22} width={22} />,
            roles: ['admin'],
            children: [
              { title: t('create communication'), path: paths.dashboard.communication.create },
              { title: t('communication logs'), path: paths.dashboard.communication.logs },
              { title: t('Linked Template'), path: paths.dashboard.communication.linked_template },
              { title: t('sender credentials'), path: paths.dashboard.communication.sender_credentials },
              { title: t('SMS Template'), path: paths.dashboard.sms_template },
              { title: t('Email Template'), path: paths.dashboard.communication.email_template },
              { title: t('WhatsApp Template'), path: paths.dashboard.communication.whatsapp_template },
              { title: t('Communication Category'), path: paths.dashboard.communication.sms_category },
            ],
          },
          {
            title: t('login logs'),
            path: paths.dashboard.login_logs,
            icon: <Iconify icon="material-symbols:login" height={22} width={22} />,
            roles: ['admin'],
          },
          {
            title: t('payment update logs'),
            path: paths.dashboard.payment_amount_update_logs,
            icon: <Iconify icon="solar:bill-list-bold" height={22} width={22} />,
            roles: ['admin'],
          },
          {
            title: t('DAT'),
            path: paths.dashboard.dat,
            icon: <Iconify icon="hugeicons:google-sheet" height={22} width={22} />,
            roles: ['admin'],
          },
          {
            title: t('Question Difficulty Configuration'),
            path: paths.dashboard.question_difficulty_configuration,
            icon: <Iconify icon="mingcute:question-fill" height={22} width={22} />,
            roles: ['admin'],
          },
          {
            title: t('Tax'),
            path: paths.dashboard.tax,
            roles: ['admin'],
            icon: <Iconify icon="tabler:receipt-tax" height={22} width={22} />,
            children: [
              { title: t('Tax'), path: paths.dashboard.tax },
            ],
          },
          {
            title: t('Hostel & Mess'),
            path: paths.dashboard.hostelmaster,
            roles: ['admin'],
            icon: <Iconify icon="carbon:building" height={22} width={22} />,
            children: [
              { title: t('configure'), path: paths.dashboard.hostelmaster },
            ],
          },
          {
            title: t('Template'),
            path: paths.dashboard.template,
            roles: ['admin'],
            icon: <Iconify icon="ic:baseline-message" height={22} width={22} />,
            children: [
              { title: t('Template'), path: paths.dashboard.template },
            ],
          },

          {
            title: t('classes'),
            path: paths.dashboard.classes,
            roles: ['admin'],
            icon: <Iconify icon="mdi:class" height={22} width={22} />,
            children: [
              { title: t('classes'), path: paths.dashboard.classes },
            ],
          },
          {
            title: t('Bank'),
            path: paths.dashboard.bank,
            roles: ['admin'],
            icon: <Iconify icon="mdi:bank" height={22} width={22} />,
            children: [
              { title: t('Bank'), path: paths.dashboard.bank },
            ],
          },
          {
            title: t('tags'),
            path: paths.dashboard.tag,
            roles: ['admin'],
            icon: <Iconify icon="ci:tag" height={22} width={22} />,
            children: [
              { title: t('tag'), path: paths.dashboard.tag },
            ],
          },
          {
            title: t('organization'),
            path: paths.dashboard.organization,
            roles: ['admin'],
            icon: <Iconify icon="octicon:organization-24" height={22} width={22} />,
            children: [
              { title: t('organization'), path: paths.dashboard.organization },
            ],
          },
          {
            title: t('Subjects'),
            path: paths.dashboard.subject,
            roles: ['admin'],
            icon: <Iconify icon="ph:bookmark" height={22} width={22} />,
            children: [
              { title: t('Subject'), path: paths.dashboard.subject },
            ],
          },
          {
            title: t('Session'),
            roles: ['admin'],
            path: paths.dashboard.session,
            icon: <Iconify icon="ph:bookmark" height={22} width={22} />,
            children: [
              { title: t('Session'), path: paths.dashboard.session },
            ],
          },
          {
            title: t('target'),
            path: paths.dashboard.target,
            roles: ['admin'],
            icon: <Iconify icon="mdi:teacher" height={22} width={22} />,
            children: [
              { title: t('target'), path: paths.dashboard.target },
            ],
          },
          {
            title: t('QR'),
            path: paths.dashboard.qr.list,
            roles: ['admin'],
            icon: <Iconify icon="ic:round-qr-code" height={22} width={22} />,
            children: [
              { title: t('QR'), path: paths.dashboard.qr.list },
            ],
          },
          {
            title: t('Discount'),
            path: paths.dashboard.discount,
            roles: ['admin'],
            icon: <Iconify icon="ic:baseline-discount" height={22} width={22} />,
            children: [
              { title: t('Discount'), path: paths.dashboard.discount },
            ],
          },
        ],
      },
    ],
    [t]
  );

  const isStudentDashboardLocked = Boolean(
    user?.is_student && (user?.institute_status === 'blocked' || user?.is_dashboard_locked)
  );

  const studentData = useMemo(
    () => [
      {
        subheader: t('management'),
        items: [
          {
            title: t('Profile'),
            path: paths.studentdashboard.studentprofile.root,
            roles: ['student'],
            disabled: isStudentDashboardLocked,
            caption: isStudentDashboardLocked ? t('Locked - Overdue Fee') : undefined,
            icon: <Iconify icon={isStudentDashboardLocked ? "solar:lock-bold" : "bxs:user"} height={22} width={22} />,
            children: isStudentDashboardLocked ? undefined : [
              { title: t('My Profile'), path: paths.studentdashboard.studentprofile.root },
            ],
          },
          {
            title: t('my learning'),
            path: paths.studentdashboard.mylearning.courses,
            roles: ['student'],
            disabled: isStudentDashboardLocked,
            caption: isStudentDashboardLocked ? t('Locked - Overdue Fee') : undefined,
            icon: <Iconify icon={isStudentDashboardLocked ? "solar:lock-bold" : "mdi:playlist-play"} height={22} width={22} />,
            children: isStudentDashboardLocked ? undefined : [
              // { title: t('dashboard'), path: paths.studentdashboard.mylearning.root },
              { title: t('My courses'), path: paths.studentdashboard.mylearning.courses },
            ],
          },
          {
            title: t('Study Material'),
            path: paths.studentdashboard.studymaterial,
            roles: ['student'],
            disabled: isStudentDashboardLocked,
            caption: isStudentDashboardLocked ? t('Locked - Overdue Fee') : undefined,
            icon: <Iconify icon={isStudentDashboardLocked ? "solar:lock-bold" : "solar:folder-with-files-bold"} height={22} width={22} />,
          },
          {
            title: t('Examination'),
            path: paths.studentdashboard.studentexamination.root,
            roles: ['student'],
            disabled: isStudentDashboardLocked,
            caption: isStudentDashboardLocked ? t('Locked - Overdue Fee') : undefined,
            icon: <Iconify icon={isStudentDashboardLocked ? "solar:lock-bold" : "healthicons:i-exam-multiple-choice-outline"} height={26} width={26} />,
            children: isStudentDashboardLocked ? undefined : [
              { title: t('list'), path: paths.studentdashboard.studentexamination.root },
              { title: t('Results'), path: paths.studentdashboard.examinationresult.root },
            ],
          },
          {
            title: t('Reattempt'),
            path: paths.studentdashboard.reattempt,
            roles: ['student'],
            disabled: isStudentDashboardLocked,
            caption: isStudentDashboardLocked ? t('Locked - Overdue Fee') : undefined,
            icon: <Iconify icon="solar:restart-bold" height={24} width={24} />,
          },
          {
            title: t('timetable'),
            roles: ['student'],
            path: paths.studentdashboard.studenttimetable.root,
            disabled: isStudentDashboardLocked,
            caption: isStudentDashboardLocked ? t('Locked - Overdue Fee') : undefined,
            icon: <Iconify icon={isStudentDashboardLocked ? "solar:lock-bold" : "ph:calendar-bold"} height={22} width={22} />,
            children: isStudentDashboardLocked ? undefined : [
              { title: t('list'), path: paths.studentdashboard.studenttimetable.list },

            ],
          },
          // {
          //   title: t('Schedule'),
          //   path: paths.studentdashboard.schedule.root,
          //   roles: ['student'],
          //   icon: <Iconify icon="mdi:playlist-play" height={22} width={22} />,
          //   children: [
          //     { title: t('Schedule'), path: paths.studentdashboard.schedule.root },
          //   ],
          // },
          // {
          //   title: t('Attendance'),
          //   path: paths.studentdashboard.attendance,
          //   roles: ['student'],
          //   icon: <Iconify icon="material-symbols:co-present" height={22} width={22} />,
          //   children: [
          //     { title: t('list'), path: paths.studentdashboard.attendance },
          //   ],
          // },
          // {
          //   title: t('Report'),
          //   roles: ['student'],
          //   path: paths.studentdashboard.studentreportcard.list,
          //   disabled: isStudentDashboardLocked,
          //   caption: isStudentDashboardLocked ? t('Locked - Overdue Fee') : undefined,
          //   icon: <Iconify icon={isStudentDashboardLocked ? "solar:lock-bold" : "carbon:report"} height={22} width={22} />,
          //   children: isStudentDashboardLocked ? undefined : [
          //     {
          //       title: t('Student Report'),
          //       path: paths.studentdashboard.studentreportcard.list,
          //       children: [
          //         { title: t('Report Card'), path: paths.studentdashboard.studentreportcard.list },
          //         { title: t('Student Test Analysis'), path: paths.studentdashboard.studenttestanalysis.list },
          //         { title: t('Question Wise Analysis'), path: paths.studentdashboard.studentqueswiseanalysis.list },
          //       ],
          //     },
          //     {
          //       title: t('Examination Report'),
          //       path: paths.studentdashboard.testresultreport.list,
          //       children: [
          //         { title: t('Test Result'), path: paths.studentdashboard.testresultreport.list },
          //         { title: t('Question Wise Analysis'), path: paths.studentdashboard.queswiseanalysis.list },
          //         { title: t('Quiz Subject Marks'), path: paths.studentdashboard.quizsubjectmarksreport.list },
          //       ],
          //     },
          //   ],
          // },

          {
            title: t('Fees'),
            path: paths.studentdashboard.payfee,
            roles: ['student'],
            icon: <Iconify icon="mynaui:rupee-square" height={22} width={22} />,
            children: [
              // { title: t('list'), path: paths.studentdashboard.feestudentcollect },
              { title: t('Pay Fee'), path: paths.studentdashboard.payfee },
            ],
          },
        ],
      },
    ],
    [t, isStudentDashboardLocked]
  );

  const parentData = useMemo(
    () => [
      {
        subheader: t('management'),
        items: [
          {
            title: t('Dashboard'),
            roles: ['parent'],
            path: paths.parentdashboard.root,
            icon: ICONS.dashboard,
          },
          {
            title: t('Attendance'),
            path: paths.parentdashboard.attendance,
            roles: ['parent'],
            icon: <Iconify icon="material-symbols:co-present" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.parentdashboard.attendance },
            ],
          },
          {
            title: t('Fees'),
            path: paths.parentdashboard.payfee,
            roles: ['parent'],
            icon: <Iconify icon="mynaui:rupee-square" height={22} width={22} />,
            children: [
              // { title: t('list'), path: paths.parentdashboard.feeparent },
              { title: t('Pay Fee'), path: paths.parentdashboard.payfee },
            ],
          },
          {
            title: t('Test Results'),
            path: paths.parentdashboard.testresults,
            roles: ['parent'],
            icon: <Iconify icon="carbon:result" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.parentdashboard.testresults },
            ],
          },
          {
            title: t('Report'),
            roles: ['parent'],
            path: paths.parentdashboard.studentreportcard.list,
            icon: <Iconify icon="carbon:report" height={22} width={22} />,
            children: [
              {
                title: t('Student Report'),
                path: paths.parentdashboard.studentreportcard.list,
                children: [
                  { title: t('Report Card'), path: paths.parentdashboard.studentreportcard.list },
                  { title: t('Student Test Analysis'), path: paths.parentdashboard.studenttestanalysis.list },
                  { title: t('Question Wise Analysis'), path: paths.parentdashboard.studentqueswiseanalysis.list },
                ],
              },
              {
                title: t('Examination Report'),
                path: paths.parentdashboard.testresultreport.list,
                children: [
                  { title: t('Test Result'), path: paths.parentdashboard.testresultreport.list },
                  { title: t('Question Wise Analysis'), path: paths.parentdashboard.queswiseanalysis.list },
                  { title: t('Quiz Subject Marks'), path: paths.parentdashboard.quizsubjectmarksreport.list },
                ],
              },
            ],
          },

        ],
      },
    ],
    [t]
  );

  const nonAcademicData = useMemo(
    () => [
      {
        subheader: t('management'),
        items: [
          {
            title: t('Dashboard'),
            roles: ['non_academic'],
            path: paths.dashboard.general.nonacademicdashboard,
            icon: ICONS.dashboard,
            alwaysShow: true,
          },
          {
            title: t('user'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.user.list,
            icon: <Iconify icon="bxs:user" height={22} width={22} />,
            moduleKey: 'user',
            permission: 'can_view',
            children: [
              { title: t('list'), path: paths.dashboard.user.list, permission: 'can_view' },
              {
                title: t('create'),
                path: paths.dashboard.user.new,
                permission: 'can_create',
                children: [
                  { title: t('academic user'), path: paths.dashboard.user.new },
                  { title: t('non-academic user'), path: paths.dashboard.user.new1 },
                ],
              },
            ],
          },
          {
            title: t('student'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.student.list,
            icon: <Iconify icon="ph:student-bold" height={22} width={22} />,
            moduleKey: 'student',
            permission: 'can_view',
            children: [
              { title: t('list'), path: paths.dashboard.student.list, permission: 'can_view' },
              { title: t('create student'), path: paths.dashboard.student.new, permission: 'can_create' },
              { title: t('create without steps'), path: paths.dashboard.student.newWithoutSteps, permission: 'can_create' },
              { title: t('register'), path: paths.dashboard.student.register, permission: 'can_create' },
              // { title: t('register-list'), path: paths.dashboard.registerlist.list, permission: 'can_create' },

              { title: t('bulk-upload'), path: paths.dashboard.student.bulkupload, permission: 'can_create' },
            ],
          },
          {
            title: t('refund'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.refund.root,
            icon: <Iconify icon="si:money-line" height={22} width={22} />,
            moduleKey: 'refund',
            permission: 'can_view',
            children: [
              { title: t('list'), path: paths.dashboard.refund.root, permission: 'can_view' },
              { title: t('create'), path: paths.dashboard.refund.new, permission: 'can_create' },
            ],
          },
          {
            title: t('courses'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.courses.root,
            icon: <Iconify icon="hugeicons:book-04" height={22} width={22} />,
            moduleKey: 'course',
            permission: 'can_view',
            children: [
              { title: t('list'), path: paths.dashboard.courses.root, permission: 'can_view' },
              { title: t('create'), path: paths.dashboard.courses.new, permission: 'can_create' },
              { title: t('configuration'), path: paths.dashboard.courses.configure, permission: 'can_configure' },
              { title: t('Add Material'), path: paths.dashboard.courses.material, permission: 'can_configure' },
              { title: t('group'), path: paths.dashboard.courseGroup.root, permission: 'can_group' },
            ],
          },
          {
            title: t('fee'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.fee.root,
            icon: <Iconify icon="si:money-line" height={22} width={22} />,
            moduleKey: 'fee',
            permission: 'can_collect',
            children: [
              { title: t('Fee-Collection'), path: paths.dashboard.fee.new, permission: 'can_collect' },
              { title: t('All Payments'), path: paths.dashboard.allpayments.list, permission: 'can_allpaysee' },
              { title: t('Approval List'), path: paths.dashboard.chequeapproval.root, permission: 'can_approve' },
            ],
          },
          {
            title: t('batch management'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.batch.list,
            icon: <Iconify icon="material-symbols:table" height={22} width={22} />,
            moduleKey: 'batch',
            permission: 'can_view',
            children: [
              { title: t('list'), path: paths.dashboard.batch.list, permission: 'can_view' },
              { title: t('create'), path: paths.dashboard.batch.new, permission: 'can_create' },
              {
                title: t('Batch Group'),
                path: paths.dashboard.batch.new,
                permission: 'can_group',
                children: [
                  { title: t('List'), path: paths.dashboard.batchgroup.list, permission: 'can_view' },
                  { title: t('Create'), path: paths.dashboard.batchgroup.new, permission: 'can_create' },
                ],
              },
              {
                title: t('teacher'),
                roles: ['admin', 'non_academic'],
                path: paths.dashboard.teacher.root,
                moduleKey: 'teacher',
                permission: 'can_view',
                children: [
                  { title: t('list'), path: paths.dashboard.teacher.allocate, permission: 'can_allocate' },
                  { title: t('Unallocate'), path: paths.dashboard.teacher.unallocate, permission: 'can_unallocate' },
                  { title: t('transfer'), path: paths.dashboard.teacher.transfer, permission: 'can_transfer' },
                  { title: t('logs'), path: paths.dashboard.teacher.log, permission: 'can_view' },
                ],
              },
            ],
          },
          {
            title: t('Examination'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.examination.root,
            icon: <Iconify icon="healthicons:i-exam-multiple-choice" height={22} width={22} />,
            moduleKey: 'examination',
            permission: 'can_view',
            children: [
              { title: t('list'), path: paths.dashboard.examination.root, permission: 'can_view' },
              { title: t('logs'), path: paths.dashboard.examination.log, permission: 'can_view' },
              { title: t('create'), path: paths.dashboard.examination.new, permission: 'can_create' },
              {
                title: t('Pattern'),
                path: paths.dashboard.pattern.new,
                moduleKey: 'pattern',
                permission: 'can_view',
                children: [
                  { title: t('List'), path: paths.dashboard.pattern.list, permission: 'can_view' },
                  { title: t('Create'), path: paths.dashboard.pattern.new, permission: 'can_create' },
                ],
              },
              {
                title: t('Exam Result'),
                path: paths.dashboard.offlineexam.new,
                moduleKey: 'result',
                permission: 'can_view',
                children: [
                  { title: t('List'), path: paths.dashboard.offlineexam.list, permission: 'can_view' },
                  { title: t('Create'), path: paths.dashboard.offlineexam.new, permission: 'can_create' },
                ],
              },
              {
                title: t('Examination Report'),
                roles: ['admin', 'non_academic'],
                path: paths.dashboard.testresultreport.list,
                // icon: <Iconify icon="carbon:report" height={22} width={22} />,
                moduleKey: 'examination report',
                permission: 'examination_report',
                children: [
                  { title: t('Test Result'), path: paths.dashboard.testresultreport.list, permission: 'test_result' },
                  { title: t('Question Wise Analysis'), path: paths.dashboard.queswiseanalysis.list, permission: 'exam_question_wise_analysis' },
                  { title: t('Quiz Subject Marks'), path: paths.dashboard.quizsubjectmarksreport.list, permission: 'test_result' },
                ],
              },
            ],
          },
          {
            title: t('talent hunt'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.talent_hunt.root,
            icon: <Iconify icon="material-symbols:trophy-outline" height={22} width={22} />,
            moduleKey: 'talenthunt',
            permission: 'can_view',
            children: [
              { title: t('create'), path: paths.dashboard.height_student.new, permission: 'can_create' },
              { title: t('talent hunt exam'), path: paths.dashboard.talent_hunt.root, permission: 'can_view' },
              { title: t('Link Talent Hunt Exams'), path: paths.dashboard.talent_hunt.link_exams, permission: 'can_update' },
              { title: t('Admit Card Instructions'), path: paths.dashboard.talent_hunt.admit_card_instructions, permission: 'can_view' },
              { title: t('students'), path: paths.dashboard.talent_hunt.students, permission: 'can_view' },
              { title: t('result'), path: paths.dashboard.talent_hunt.result, permission: 'can_result' },
              { title: t('Schools'), path: paths.dashboard.school, permission: 'can_view' },
              { title: t('Talent Hunt Centers'), path: paths.dashboard.center, permission: 'can_view' },
            ],
          },
          {
            title: t('syllabus'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.syllabus.root,
            icon: <Iconify icon="hugeicons:task-edit-01" height={22} width={22} />,
            moduleKey: 'syllabus',
            permission: 'can_view',
            children: [
              { title: t('list'), path: paths.dashboard.syllabus.list, permission: 'can_view' },
              { title: t('create'), path: paths.dashboard.syllabus.new, permission: 'can_create' },
              { title: t('logs'), path: paths.dashboard.syllabus.logs, permission: 'can_view' },
            ],
          },
          {
            title: t('Question Bank'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.questionbank.root,
            icon: <Iconify icon="si:money-line" height={22} width={22} />,
            moduleKey: 'questionbank',
            permission: 'can_view',
            children: [
              { title: t('list'), path: paths.dashboard.questionbank.root, permission: 'can_view' },
              { title: t('setup'), path: paths.dashboard.questionbank.new, permission: 'can_create' },
              { title: t('tasks'), path: paths.dashboard.task.list, moduleKey: 'task', permission: 'tasks_view' },
              { title: t('logs'), path: paths.dashboard.questionbank.logs, permission: 'can_view' },
            ],
          },
          {
            title: t('Register'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.register.root,
            icon: <Iconify icon="healthicons:register-book" height={22} width={22} />,
            moduleKey: 'register',
            permission: 'can_view',
            children: [
              { title: t('list'), path: paths.dashboard.register.root, permission: 'can_view' },
            ],
          },
          {
            title: t('Cash Register'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.cashregister.root,
            icon: <Iconify icon="mdi:cash" height={22} width={22} />,
            moduleKey: 'cash register',
            permission: 'can_view',
            children: [
              { title: t('Transaction'), path: paths.dashboard.cashregister.root, permission: 'can_create' },
              { title: t('reconciliation'), path: paths.dashboard.cashregister.reconciliation, permission: 'can_update' },
              { title: t('logs'), path: paths.dashboard.cashregister.logs, permission: 'can_view' },
              { title: t('Withdrawal Approvals'), path: paths.dashboard.cashregister.approvals, permission: 'can_approve' },
              { title: t('Withdrawal Requests'), path: paths.dashboard.cashregister.requests, permission: 'can_withdraw' },
            ],
          },
          {
            title: t('Financial Report'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.report.list,
            icon: <Iconify icon="carbon:report" height={22} width={22} />,
            moduleKey: 'financial report' || 'gst report',
            permission: 'financial_report',
            children: [
              { title: t('Cash Register Report'), path: paths.dashboard.report.list, permission: 'cash_register_report' },
              { title: t('GST Report'), path: paths.dashboard.gstreport.list, permission: 'gst_report' },
              { title: t('Daily Collection Report'), path: paths.dashboard.dailycollectionreport.list, permission: 'daily_collection_report' },
            ],
          },
          {
            title: t('Student Report'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.studentbalancereport.list,
            icon: <Iconify icon="carbon:report" height={22} width={22} />,
            moduleKey: 'student report',
            permission: 'student_report',
            children: [
              { title: t('Balance Report'), path: paths.dashboard.studentbalancereport.list, permission: 'balance_report' },
              { title: t('Hostel & Mess Balance Report'), path: paths.dashboard.hostel_mess_balance_report.list, permission: 'hostel_mess_balance_report' },
              { title: t('Report Card'), path: paths.dashboard.studentreportcard.list, permission: 'report_card' },
              { title: t('Student Test Analysis'), path: paths.dashboard.studenttestanalysis.list, permission: 'student_test_analysis' },
              { title: t('Question Wise Analysis'), path: paths.dashboard.studentqueswiseanalysis.list, permission: 'student_question_wise_analysis' },
            ],
          },
          {
            title: t('Admission Report'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.addmissionsummary.list,
            icon: <Iconify icon="carbon:report" height={22} width={22} />,
            moduleKey: 'admission report',
            permission: 'admission_report',
            children: [
              { title: t('Admission Summary'), path: paths.dashboard.addmissionsummary.list, permission: 'admission_summary' },
            ],
          },
          {
            title: t('Batch Report'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.batchtopper.list,
            icon: <Iconify icon="carbon:report" height={22} width={22} />,
            moduleKey: 'batch report',
            permission: 'batch_report',
            children: [
              { title: t('Batch Topper'), path: paths.dashboard.batchtopper.list, permission: 'batch_topper' },
            ],
          },
          {
            title: t('Examination Report'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.testresultreport.list,
            icon: <Iconify icon="carbon:report" height={22} width={22} />,
            moduleKey: 'examination report',
            permission: 'examination_report',
            children: [
              { title: t('Test Result'), path: paths.dashboard.testresultreport.list, permission: 'test_result' },
              { title: t('Question Wise Analysis'), path: paths.dashboard.queswiseanalysis.list, permission: 'exam_question_wise_analysis' },
              { title: t('Quiz Subject Marks'), path: paths.dashboard.quizsubjectmarksreport.list, permission: 'test_result' },
            ],
          },
          {
            title: t('Task List'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.task.list,
            icon: <Iconify icon="fluent-mdl2:task-manager" height={22} width={22} />,
            moduleKey: 'task',
            permission: 'tasks_view',
            children: [
              { title: t('tasks'), path: paths.dashboard.task.list, moduleKey: 'task', permission: 'tasks_view' },
            ],
          },
          {
            title: t('Tax'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.tax,
            icon: <Iconify icon="tabler:receipt-tax" height={22} width={22} />,
            moduleKey: 'tax',
            permission: 'can_view',
            children: [
              { title: t('Tax'), path: paths.dashboard.tax, permission: 'can_view' },
            ],
          },
          {
            title: t('Building'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.building.root,
            icon: <Iconify icon="fluent:building-multiple-24-regular" height={22} width={22} />,
            moduleKey: 'building',
            permission: 'can_view',
            children: [
              { title: t('list'), path: paths.dashboard.building.root, permission: 'can_view' },
              { title: t('create'), path: paths.dashboard.building.new, permission: 'can_create' },
            ],
          },
          {
            title: t('Hostel'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.hostel.root,
            icon: <Iconify icon="ph:building-bold" height={22} width={22} />,
            moduleKey: 'hostel',
            permission: 'can_view',
            children: [
              { title: t('list'), path: paths.dashboard.hostel.root, permission: 'can_view' },
              { title: t('create building'), path: paths.dashboard.hostel.new, permission: 'can_create' },
              { title: t('allocation'), path: paths.dashboard.hostel.allocation, permission: 'can_allocate' },
              { title: t('allocation-list'), path: paths.dashboard.hostel.allocationlist, permission: 'can_view' },
              { title: t('vacated-bed-list'), path: paths.dashboard.hostel.vacatedbedlist, permission: 'can_view' },
              { title: t('vacate-refund-list'), path: paths.dashboard.hostel.vacate_refund_list, moduleKey: 'refund', permission: 'can_view' },
              
              {
                title: t('logs'),
                path: paths.dashboard.hostel.logs,
                permission: 'can_view',
                children: [
                  { title: t('Hostel'), path: paths.dashboard.hostel.hostellogs, permission: 'can_view' },
                  { title: t('Allocation'), path: paths.dashboard.hostel.logs, permission: 'can_view' },
                  { title: t('Warden'), path: paths.dashboard.hostel.wardenlogs, permission: 'can_view' },
                ],
              },
              {
                title: t('outsider'),
                path: paths.dashboard.outsider.list,
                permission: 'can_view',
                children: [
                  { title: t('list'), path: paths.dashboard.outsider.list, permission: 'can_view' },
                  { title: t('create'), path: paths.dashboard.outsider.new, permission: 'can_create' },
                  { title: t('logs'), path: paths.dashboard.outsider.logs, permission: 'can_view' },
                ],
              },
            ],
          },
          {
            title: t('Hostel & Mess'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.hostelmaster,
            icon: <Iconify icon="carbon:building" height={22} width={22} />,
            moduleKey: 'hostel & mess',
            permission: 'can_view',
            children: [
              { title: t('configure'), path: paths.dashboard.hostelmaster, permission: 'can_configure' },
              {
                title: t('Hostel & Mess Balance Report'),
                path: paths.dashboard.hostel_mess_balance_report.list,
                moduleKey: 'student report',
                permission: 'hostel_mess_balance_report',
              },
            ],
          },
          {
            title: t('Attendance'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.attendanceadmin.list,
            icon: <Iconify icon="ph:student-bold" height={22} width={22} />,
            moduleKey: 'attendance',
            permission: 'can_view',
            children: [
              { title: t('list'), path: paths.dashboard.attendanceadmin.list, permission: 'can_view' },
            ],
          },
          {
            title: t('Template'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.template,
            icon: <Iconify icon="ic:baseline-message" height={22} width={22} />,
            moduleKey: 'template',
            permission: 'can_view',
            children: [
              { title: t('Template'), path: paths.dashboard.template, permission: 'can_view' },
            ],
          },

          {
            title: t('classes'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.classes,
            icon: <Iconify icon="mdi:class" height={22} width={22} />,
            moduleKey: 'classes',
            permission: 'can_view',
            children: [
              { title: t('classes'), path: paths.dashboard.classes, permission: 'can_view' },
            ],
          },
          {
            title: t('Bank'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.bank,
            icon: <Iconify icon="mdi:bank" height={22} width={22} />,
            moduleKey: 'bank',
            permission: 'can_view',
            children: [
              { title: t('Bank'), path: paths.dashboard.bank, permission: 'can_view' },
            ],
          },
          {
            title: t('tags'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.tag,
            icon: <Iconify icon="ci:tag" height={22} width={22} />,
            moduleKey: 'tags',
            permission: 'can_view',
            children: [
              { title: t('tag'), path: paths.dashboard.tag, permission: 'can_view' },
            ],
          },
          {
            title: t('Subjects'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.subject,
            icon: <Iconify icon="ph:bookmark" height={22} width={22} />,
            moduleKey: 'subjects',
            permission: 'can_view',
            children: [
              { title: t('Subject'), path: paths.dashboard.subject, permission: 'can_view' },
            ],
          },

          {
            title: t('target'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.target,
            icon: <Iconify icon="mdi:teacher" height={22} width={22} />,
            moduleKey: 'target',
            permission: 'can_view',
            children: [
              { title: t('target'), path: paths.dashboard.target, permission: 'can_view' },
            ],
          },
          {
            title: t('Discount'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.discount,
            icon: <Iconify icon="ic:baseline-discount" height={22} width={22} />,
            moduleKey: 'discount',
            permission: 'can_view',
            children: [
              { title: t('Discount'), path: paths.dashboard.discount, permission: 'can_view' },
            ],
          },
          {
            title: t('Roles'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.role,
            icon: <Iconify icon="eos-icons:role-binding" height={22} width={22} />,
            moduleKey: 'roles',
            permission: 'can_view',
          },
          {
            title: t('communication'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.communication.root,
            icon: <Iconify icon="healthicons:communication" height={22} width={22} />,
            moduleKey: 'communication logs',
            permission: 'can_view',
            children: [
              { title: t('create communication'), path: paths.dashboard.communication.create, permission: 'can_create' },
              { title: t('communication logs'), path: paths.dashboard.communication.logs, permission: 'can_view' },
              { title: t('Linked Template'), path: paths.dashboard.communication.linked_template, permission: 'can_view' },
              { title: t('SMS Template'), path: paths.dashboard.sms_template, permission: 'can_view' },
              { title: t('SMS Category'), path: paths.dashboard.communication.sms_category, permission: 'can_view' },
            ],
          },
          {
            title: t('DAT'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.dat,
            icon: <Iconify icon="hugeicons:google-sheet" height={22} width={22} />,
            moduleKey: 'dat',
            permission: 'can_view',
          },
          {
            title: t('Question Difficulty Configuration'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.question_difficulty_configuration,
            icon: <Iconify icon="mingcute:question-fill" height={22} width={22} />,
            moduleKey: 'question difficulty',
            permission: 'can_view',
          },
          {
            title: t('timetable'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.timetable ? paths.dashboard.timetable.root : '/dashboard/timetable',
            icon: <Iconify icon="ph:calendar-bold" height={22} width={22} />,
            moduleKey: 'timetable',
            permission: 'can_view',
            children: [
              { title: t('list'), path: paths.dashboard.timetable ? paths.dashboard.timetable.list : '/dashboard/timetable/list', permission: 'can_view' },
            ],
          },
          // {
          //   title: t('Assignment'),
          //   roles: ['admin', 'non_academic'],
          //   path: paths.academicdashboard?.assignment?.root || '/dashboard/assignment',
          //   icon: <Iconify icon="si:assignment-line" height={22} width={22} />,
          //   moduleKey: 'assignment',
          //   permission: 'can_view',
          //   children: [
          //     { title: t('list'), path: paths.academicdashboard?.assignment?.list || '/dashboard/assignment/list', permission: 'can_view' },
          //   ],
          // },
          {
            title: t('Session'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.session,
            icon: <Iconify icon="ph:bookmark" height={22} width={22} />,
            moduleKey: 'session',
            permission: 'can_view',
          },
          {
            title: t('Organization'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.organization,
            icon: <Iconify icon="octicon:organization-24" height={22} width={22} />,
            moduleKey: 'organization',
            permission: 'can_view',
          },
          {
            title: t('QR'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.qr.list,
            icon: <Iconify icon="ic:round-qr-code" height={22} width={22} />,
            moduleKey: 'qr',
            permission: 'can_view',
          },
          {
            title: t('Outsider'),
            roles: ['admin', 'non_academic'],
            path: paths.dashboard.outsider.root,
            icon: <Iconify icon="mdi:account-outline" height={22} width={22} />,
            moduleKey: 'outsider',
            permission: 'can_view',
            children: [
              { title: t('list'), path: paths.dashboard.outsider.list, permission: 'can_view' },
              { title: t('create'), path: paths.dashboard.outsider.new, permission: 'can_create' },
              { title: t('logs'), path: paths.dashboard.outsider.logs, permission: 'can_view' },
            ],
          },
        ],
      },
    ],
    [t]
  );

  const heightstudentData = useMemo(
    () => [
      {
        subheader: t('management'),
        items: [
          {
            title: t('Dashboard'),
            path: paths.heightstudentdashboard.root,
            roles: ['height_student'],
            icon: ICONS.dashboard,
          },
          {
            title: t('Admit Card'),
            path: paths.heightstudentdashboard.admitcard,
            roles: ['height_student'],
            icon: <Iconify icon="solar:card-2-bold" height={24} width={24} />,
          },
          {
            title: t('Downloads'),
            path: paths.heightstudentdashboard.downloads,
            roles: ['height_student'],
            icon: <Iconify icon="solar:download-bold" height={24} width={24} />,
          },
          {
            title: t('Examination'),
            path: paths.heightstudentdashboard.studentexamination.root,
            roles: ['height_student'],
            icon: <Iconify icon="healthicons:i-exam-multiple-choice-outline" height={26} width={26} />,
            children: [
              { title: t('list'), path: paths.heightstudentdashboard.studentexamination.root },
              ...(isHeightResultDeclared
                ? [{ title: t('Results'), path: paths.heightstudentdashboard.examinationresult.root }]
                : []),
            ],
          },
        ],
      },
    ],
    [t, isHeightResultDeclared]
  );

  const outsiderData = useMemo(
    () => [
      {
        subheader: t('management'),
        items: [
          {
            title: t('Dashboard'),
            path: paths.outsiderdashboard.root,
            roles: ['outsider'],
            icon: ICONS.dashboard,
          },
          {
            title: t('Fees'),
            path: paths.outsiderdashboard.fee.root,
            roles: ['outsider'],
            icon: <Iconify icon="mynaui:rupee-square" height={22} width={22} />,
            children: [
              { title: t('list'), path: paths.outsiderdashboard.fee.root },
            ],
          },
        ],
      },
    ],
    [t]
  );

  const guestData = useMemo(
    () => [
      {
        subheader: t('management'),
        items: [
          {
            title: t('Dashboard'),
            path: paths.guestdashboard.root,
            roles: ['guest'],
            icon: ICONS.dashboard,
          },
        ],
      },
    ],
    [t]
  );

  const filteredData = useMemo(() => {
    let data = [];
    if (!user) {
      console.warn('User is undefined');
      return [];
    }

    if (user.is_admin) return adminData;
    if (user.is_academic) return academicData;
    if (user.is_student) return studentData;
    if (user.is_parent) return parentData;
    if (user.is_height_student) return heightstudentData;
    if (user.is_outsider) return outsiderData;
    if (user.is_guest) return guestData;

    if (user.is_non_academic) {
      return nonAcademicData.map((section) => {
        const items = section.items.reduce((acc, item) => {
          if (item.alwaysShow) {
            acc.push(item);
            return acc;
          }

          const moduleName = item.moduleKey || getModuleName(item.title);
          if (!moduleName) return acc;

          const hasParentPermission = item.permission ? hasPermission(moduleName, item.permission) : true;

          // If item has children, filter children by their permissions and include item only if any child remains
          if (item.children) {
            const children = item.children
              .map((child) => {
                const childModuleName = child.moduleKey || moduleName;
                const hasChildPermission = child.permission
                  ? hasPermission(childModuleName, child.permission)
                  : true;
                if (!hasChildPermission) return null;

                if (child.children) {
                  const grandChildren = child.children.filter((grandchild) => {
                    const grandchildModuleName = grandchild.moduleKey || childModuleName;
                    return grandchild.permission ? hasPermission(grandchildModuleName, grandchild.permission) : true;
                  });
                  if (grandChildren.length === 0) return null;
                  return { ...child, children: grandChildren };
                }

                return child;
              })
              .filter(Boolean);

            if (children.length > 0) {
              acc.push({ ...item, children });
            } else if (hasParentPermission) {
              // include parent if parent permission allows even without visible children
              const copy = { ...item };
              delete copy.children;
              acc.push(copy);
            }

            return acc;
          }

          // No children: include only if parent permission allows
          if (hasParentPermission) acc.push(item);
          return acc;
        }, []);

        return { ...section, items };
      });
    }

    console.warn('No valid role found for user:', user);
    return [];
  }, [user, adminData, academicData, studentData, parentData, nonAcademicData, heightstudentData, outsiderData, guestData]);

  return filteredData;
}
